import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrainingAccess } from '@/features/agents';
import { ShimmerCard, ShimmerProgressBar, ShimmerNavigation } from '@/shared/components';

interface TrainingGateProps {
  moduleType: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showTrainingLink?: boolean;
}

export const TrainingGate: React.FC<TrainingGateProps> = ({
  moduleType,
  children,
  fallback,
  showTrainingLink = true,
}) => {
  const { hasAccess, loading } = useTrainingAccess(moduleType);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6 px-4 sm:px-6 md:px-8 lg:px-8">
        {/* Shimmer Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="h-6 sm:h-7 md:h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
        </div>

        {/* Shimmer Progress Bar */}
        <ShimmerProgressBar />

        {/* Shimmer Main Content */}
        <ShimmerCard />

        {/* Shimmer Navigation */}
        <ShimmerNavigation />
      </div>
    );
  }

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Training Required
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                You need to complete the required training modules before accessing this feature.
              </p>
              {showTrainingLink && (
                <p className="mt-2">
                  <button
                    onClick={() => {
                      navigate('/dashboard#training');
                      // Also trigger the tab change event for the dashboard
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('navigate:training'));
                      }, 100);
                    }}
                    className="font-medium underline hover:text-yellow-600 text-left"
                  >
                    Go to Training
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
